import React, { useState, useEffect } from "react";
import { Clock } from "lucide-react";

export function TimeFilterPicker({
  value,
  onChange,
  disabled,
  className,
}: {
  value?: string;
  onChange?: (time: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const [hour, setHour] = useState("--");
  const [minute, setMinute] = useState("--");
  const [period, setPeriod] = useState<"AM" | "PM" | "">("");
  const [openField, setOpenField] = useState<"hour" | "minute" | "period" | null>(null);

  const hours12 = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);
  const periods: ("AM" | "PM")[] = ["AM", "PM"];

  const handleChange = (h = hour, m = minute, p = period) => {
    // If any field is empty, placeholder, or invalid, clear the filter
    if (!h || !m || h === "--" || m === "--" || isNaN(Number(h)) || isNaN(Number(m)) || !p) {
      onChange?.("");
      return;
    }

    let finalHour = parseInt(h);
    
    // Convert 12-hour to 24-hour for storage
    if (p === "AM" && finalHour === 12) {
      finalHour = 0;
    } else if (p === "PM" && finalHour !== 12) {
      finalHour += 12;
    }
    
    const result = `${finalHour.toString().padStart(2, '0')}:${m}`;
    onChange?.(result);
  };

  const handlePeriodChange = (newPeriod: "AM" | "PM" | "") => {
    setPeriod(newPeriod);
    handleChange(hour, minute, newPeriod);
  };

  useEffect(() => {
    if (!value) {
      // Clear all fields when value is empty
      setHour("--");
      setMinute("--");
      setPeriod("");
      return;
    }
    
    const timeParts = value.split(':');
    if (timeParts.length >= 2) {
      const h = parseInt(timeParts[0]);
      const m = timeParts[1];
      
      // Convert 24-hour to 12-hour for display
      let displayHour = h;
      let displayPeriod: "AM" | "PM" = "AM";
      
      if (h === 0) {
        displayHour = 12;
        displayPeriod = "AM";
      } else if (h === 12) {
        displayHour = 12;
        displayPeriod = "PM";
      } else if (h > 12) {
        displayHour = h - 12;
        displayPeriod = "PM";
      } else {
        displayHour = h;
        displayPeriod = "AM";
      }
      
      setHour(displayHour.toString());
      setMinute(m);
      setPeriod(displayPeriod);
    }
  }, [value]);

  return (
    <div className={`flex justify-end items-center relative border rounded-md w-full h-10 px-3 py-2 text-sm ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
         style={{ 
           borderColor: '#d1d5db',
           boxShadow: 'none',
           outline: 'none'
         }}>
      {/* Clock icon */}
      <Clock className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: 'black !important' }} />
      
      <div className="flex items-center gap-1">
        {/* Hour input */}
        <div className="relative">
          <input
            value={hour}
            disabled={disabled}
            onFocus={() => setOpenField("hour")}
                    onChange={(e) => {
                      let v = e.target.value.replace(/\D/g, "").slice(0, 2);
                      
                      if (v) {
                        const num = parseInt(v);
                        if (num < 1) v = "1";
                        else if (num > 12) v = "12";
                      } else {
                        v = "--";
                      }
        
                      setHour(v);
                      handleChange(v, minute, period);
                    }}
            onBlur={(e) => {
              let v = e.target.value.replace(/\D/g, "").slice(0, 2);
              setTimeout(() => setOpenField(null), 150);
              if (!v) v = "--";
              setHour(v);
              handleChange(v, minute, period);
            }}
            className="w-6 text-center border-none rounded-md bg-transparent focus:outline-none text-black"
            placeholder="--"
          />
          {openField === "hour" && (
            <div className="absolute right-0 z-10 bg-white border rounded-md shadow-md mt-1 max-h-32 overflow-y-auto scrollbar-hide min-w-12">
              {/* Clear option */}
              <div
                onMouseDown={() => {
                  setHour("--");
                  handleChange("--", minute, period);
                  setOpenField(null);
                }}
                className="px-3 py-1 hover:bg-gray-100 cursor-pointer text-center text-sm text-gray-500"
              >
                Clear
              </div>
              {hours12.map((h) => (
                <div
                  key={h}
                  onMouseDown={() => {
                    setHour(h.toString());
                    handleChange(h.toString(), minute, period);
                    setOpenField(null);
                  }}
                  className="px-3 py-1 hover:bg-gray-100 cursor-pointer text-center text-sm"
                >
                  {h}
                </div>
              ))}
            </div>
          )}
        </div>

        <span className="px-1 text-black">:</span>

        {/* Minute input */}
        <div className="relative">
          <input
            value={minute}
            disabled={disabled}
            onFocus={() => setOpenField("minute")}
                    onChange={(e) => {
                      let v = e.target.value.replace(/\D/g, "").slice(0, 2);
        
                      if (v) {
                        if (Number(v) < 0) v = "00";
                        if (Number(v) > 59) v = "59";
                      } else {
                        v = "--";
                      }
        
                      setMinute(v);
                      handleChange(hour, v, period);
                    }}
            onBlur={(e) => {
              let v = e.target.value.replace(/\D/g, "").slice(0, 2);
              setTimeout(() => setOpenField(null), 150);
              if (!v) v = "--";
              else if (v && Number(v) < 10) v = v.padStart(2, "0");
              setMinute(v);
              handleChange(hour, v, period);
            }}
            className="w-6 text-center border-none rounded-md bg-transparent focus:outline-none text-black"
            placeholder="--"
          />
          {openField === "minute" && (
            <div className="absolute right-0 z-10 bg-white border rounded-md shadow-md mt-1 max-h-32 overflow-y-auto scrollbar-hide min-w-12">
              {/* Clear option */}
              <div
                onMouseDown={() => {
                  setMinute("--");
                  handleChange(hour, "--", period);
                  setOpenField(null);
                }}
                className="px-3 py-1 hover:bg-gray-100 cursor-pointer text-center text-sm text-gray-500"
              >
                Clear
              </div>
              {minutes.map((m) => (
                <div
                  key={m}
                  onMouseDown={() => {
                    const displayMinute = m.toString().padStart(2, "0");
                    setMinute(displayMinute);
                    handleChange(hour, displayMinute, period);
                    setOpenField(null);
                  }}
                  className="px-3 py-1 hover:bg-gray-100 cursor-pointer text-center text-sm"
                >
                  {m.toString().padStart(2, "0")}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AM/PM selector */}
        <span className="px-1 text-black"> </span>
        <div className="relative">
          <input
            value={period}
            disabled={disabled}
            onFocus={() => setOpenField("period")}
            onChange={(e) => {
              // Allow clearing by typing or backspace
              if (e.target.value === "" || e.target.value === "--") {
                handlePeriodChange("");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Backspace" || e.key === "Delete") {
                handlePeriodChange("");
                setOpenField(null);
              }
            }}
            className="w-8 text-center border-none rounded-md bg-transparent focus:outline-none text-black cursor-pointer"
            placeholder="--"
          />
          {openField === "period" && (
            <div className="absolute right-0 z-10 bg-white border rounded-md shadow-md mt-1 min-w-12">
              {/* Clear option */}
              <div
                onMouseDown={() => {
                  handlePeriodChange("");
                  setOpenField(null);
                }}
                className="px-3 py-1 hover:bg-gray-100 cursor-pointer text-center text-sm text-gray-500"
              >
                Clear
              </div>
              {periods.map((p) => (
                <div
                  key={p}
                  onMouseDown={() => {
                    handlePeriodChange(p);
                    setOpenField(null);
                  }}
                  className="px-3 py-1 hover:bg-gray-100 cursor-pointer text-center text-sm"
                >
                  {p}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
